/**
 * React Query hook for lazy-loading cache debug data.
 *
 * @module cache-debug/useCacheDebugData
 */
import { useQuery } from '@tanstack/react-query';
import type { CacheFormationDiagnostics } from '@chatsdet/types';
import { API_URL } from '../../../../../config';

export type CacheDebugAction = {
  index: number;
  actionType: string;
  elementDescription: string | null;
  selectorAuthor: 'deterministic' | 'agentic' | null;
  selectorAgentFailureReason: string | null;
  requiresAgent: boolean;
  selectors: Record<string, unknown> | null;
};

export type CacheDebugSnapshot = {
  failedActionIndex: number;
  failedActionType: string;
  elementDescription: string | null;
  attemptedSelectors: unknown;
  resolutionResult: unknown;
  pageUrl: string | null;
};

export type CacheDebugStepOutcome = {
  type: 'cached' | 'gap';
  stepOrder: number;
  success: boolean;
  error?: string;
  recoveredVia?: 'recovery' | 'gap';
};

export type CacheDebugEntryTrust = {
  state: 'unproven' | 'verified' | 'suspect';
  streak: number;
  softFailures: number;
  updatedAt: string;
};

export type CacheDebugCompiledEntry =
  | { type: 'cached'; stepOrder: number; actionIndices: number[]; trust?: CacheDebugEntryTrust }
  | { type: 'gap'; stepOrder: number };

export type CacheDebugPlaybookStep = {
  order: number;
  action: string;
  target: string;
  actionRefs?: number[];
};

export type CacheDebugPreEjectionSnapshot = {
  playbook: CacheDebugPlaybookStep[];
  cacheSummary: {
    cacheId: string;
    /** Row lifecycle at ejection. Snapshots persisted before 2026-09-15 carry a legacy `playbookHealth` string instead. */
    playbookState?: 'unverified' | 'verified' | 'suspect' | 'retired';
    playbookHealth?: string | null;
    compilationScore: number | null;
    actionCount: number;
    compiledEntryTypes: Array<'cached' | 'gap'> | null;
    stepFailureCounts: Record<string, number> | null;
  };
};

export type CacheDebugStep = {
  stepId: string;
  nodeId: string;
  nodeName: string;
  nodeType: string;
  status: string;
  cacheOutcome: string | null;
  cacheHitSource: string | null;
  cacheOutcomeReason: string | null;
  stepOutcomes: CacheDebugStepOutcome[] | null;
  stepDrivenReplay: boolean;
  cache: {
    id: string;
    playbookHealth: 'verified' | 'degraded' | 'stale' | null;
    compilationScore: number | null;
    formationDiagnostics: CacheFormationDiagnostics | null;
    stepFailureCounts: Record<string, number> | null;
    compiledEntries: CacheDebugCompiledEntry[] | null;
    actions: CacheDebugAction[];
  } | null;
  playbookSteps: CacheDebugPlaybookStep[] | null;
  preEjectionSnapshot: CacheDebugPreEjectionSnapshot | null;
  debugSnapshots: CacheDebugSnapshot[];
  dataScripts: Array<{
    label: string;
    code: string;
    tableName: string;
    tableRef?: string;
    createdAt: string;
  }> | null;
};

type CacheDebugData = {
  steps: CacheDebugStep[];
};

export function useCacheDebugData(
  workflowId: string | undefined,
  runId: string | undefined,
  enabled: boolean = false
) {
  return useQuery<CacheDebugData>({
    queryKey: ['workflow-run-cache-debug', workflowId, runId],
    queryFn: async () => {
      if (!workflowId || !runId) return { steps: [] };

      const res = await fetch(`${API_URL}/workflows/${workflowId}/runs/${runId}/cache-debug`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load cache debug data');

      const json = await res.json();
      if (!json.ok || !json.data) return { steps: [] };

      return json.data;
    },
    enabled: enabled && Boolean(workflowId) && Boolean(runId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}
